import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Item, Person, TripView, WeatherDay } from "./api.ts";
import { plannable } from "./ask-ai.ts";
import { DayWeather, WeatherCredit } from "./DayWeather.tsx";
import { Picks } from "./Details.tsx";
import { type Block, type DayRow, type GapRow, type Plan, plan } from "./dayplan.ts";
import { kindClass, kindOf, localToday, originLabel, waitingSuggestion, wantsLine } from "./format.ts";
import { useKept, useKeptScroll } from "./kept.ts";
import { litProps, useLastOpened } from "./opened.ts";
import { PlanDayForm } from "./PlanDay.tsx";
import type { Choice } from "./plan.ts";
import { useReadOnly } from "./readOnly.ts";
import { AvatarStack, Icon, SegmentedControl } from "./souvenir.ts";
import { FirstVisit, viewPrompt } from "./Tips.tsx";
import { inTodayWindow } from "./today.ts";
import { Empty, factLine, KindIcon, SectionTitle } from "./ui.tsx";
import { current, type Unit, unitFor } from "./weather.ts";

// The trip day by day. Each day: its date and where we sleep, then what happens, part by part of the
// day. Firm things (a time, or booked) are solid; fuzzy ones are dashed. What has no day yet sits
// beside (desktop) or behind a switch (phone). Tapping anything opens its card over Days.

interface Props {
  trip: TripView;
  // What's in the plan (options left out until picked), and the choices, to show the undecided ones.
  items: Item[];
  choices: Choice[];
  // Each day's weather for where we are, once it has loaded (none offline, unless the phone kept it).
  weather: WeatherDay[] | null;
  wide: boolean;
  onOpen: (id: string) => void;
  // Something planned for a day here (Wanted, not placed): load the trip again.
  onChanged: () => void;
  // Its Add (AskAi.tsx), at the top right of its title (a computer's; on a phone the switch stands in for it, and
  // Add floats above the tabs). None on a read-only trip.
  add?: ReactNode;
  // Plan for a day, chosen from Add: No day yet shows (on a phone) and its first Plan for a day takes the focus.
  start?: boolean;
  onStarted?: () => void;
}

const WEEKDAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const SHORT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const fmt = (f: Intl.DateTimeFormat, date: string) => f.format(new Date(`${date}T00:00:00Z`));

export function Days({
  trip,
  items,
  choices,
  weather,
  wide,
  onOpen,
  onChanged,
  add = null,
  start = false,
  onStarted,
}: Props) {
  const laid = plan(
    trip,
    items,
    choices.filter((c) => !c.picked).map((c) => ({ choice: c.choice, options: c.options.length })),
  );
  // °C, or °F where the browser's region reads it.
  const unit = useMemo(
    () =>
      unitFor({
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        languages: navigator.languages?.length ? navigator.languages : [navigator.language],
      }),
    [],
  );
  const now = new Date();
  // During the trip (Today's window), Days opens on today, as I first come to it in the trip; kept after that
  // where I leave it, as every list is (kept.ts).
  const todayDate = localToday(now);
  const today = inTodayWindow(trip.dates, todayDate) ? todayDate : null;
  const forDay = current(weather ?? [], now);
  const shown = laid.rows.flatMap((r) => (r.kind === "day" && r.date && forDay.get(r.date)) || []);
  const forecasts = shown.flatMap((d) => (d.kind === "forecast" ? [new Date(d.checked_at)] : []));
  const oldest = forecasts.length > 0 ? new Date(Math.min(...forecasts.map((d) => d.getTime()))) : null;
  // Which half a phone shows, and where each list was: Back puts them back.
  const [mode, setMode] = useKept<"days" | "none">("days-mode", "days");
  const main = useRef<HTMLElement>(null);
  const side = useRef<HTMLElement>(null);
  const phone = useRef<HTMLDivElement>(null);
  const onToday = today ? "[data-today]" : undefined;
  useKeptScroll(main, "days", undefined, onToday);
  useKeptScroll(side, "days-none");
  useKeptScroll(phone, `days-phone-${mode}`, undefined, mode === "days" ? onToday : undefined);
  const waiting = laid.sometime.length + laid.untimed.length;
  // Nothing has a day yet (the trip's dates may still show, as days with nothing planned): Days says
  // why, and how it fills in, instead of its first-visit card, which would say the same.
  const empty = !laid.rows.some((r) => r.kind === "day");
  // A read-only trip's headings drop their "yet": nothing more gets a day while it can't change.
  const readOnly = useReadOnly();
  const noDay = readOnly ? "No day" : "No day yet";
  // biome-ignore lint/correctness/useExhaustiveDependencies: once each time Plan for a day is chosen
  useEffect(() => {
    if (!start) return;
    if (!wide) setMode("none");
    const frame = requestAnimationFrame(() => {
      const first = (wide ? side : phone).current?.querySelector<HTMLElement>(".tm-plan-line");
      first?.scrollIntoView({ block: "center" });
      first?.focus({ preventScroll: true });
      onStarted?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [start]);

  const days = (
    <section className="tm-days" aria-label="The trip, day by day">
      {empty && (
        <div className="tm-days-empty">
          <h2 className="title">{readOnly ? "Nothing has a day" : "Nothing has a day yet"}</h2>
          <Empty prompt={viewPrompt("days", trip.title)} readOnly="Things with a day show here, the rest under No day.">
            Your days fill in as plans firm up: things with a day show here, the rest under No day yet. Plan a place for
            a day from its card or from No day yet, or ask your AI to fit what you all want into your days.
          </Empty>
        </div>
      )}
      {laid.rows.map((row) =>
        row.kind === "gap" ? (
          <Gap key={`gap-${row.first}`} row={row} numbered={laid.numbered} today={today} />
        ) : (
          <Day
            key={row.n}
            row={row}
            plan={laid}
            trip={trip}
            weather={row.date ? forDay.get(row.date) : undefined}
            unit={unit}
            now={now}
            today={row.date !== null && row.date === today}
            wide={wide}
            onOpen={onOpen}
          />
        ),
      )}
      {shown.length > 0 && <WeatherCredit oldest={oldest} now={now} />}
    </section>
  );
  const tip = <FirstVisit view="days" hidden={empty} className="tm-days-tip" />;
  const unscheduled = <Unscheduled plan={laid} trip={trip} items={items} onOpen={onOpen} onChanged={onChanged} />;

  if (wide) {
    return (
      <div className="tm-days-split">
        <main className="tm-days-main" ref={main}>
          <div className="tm-view-title tm-days-title">
            <h1 className="display-m">Days</h1>
            {add}
          </div>
          {tip}
          {days}
        </main>
        <aside className="tm-days-side" aria-labelledby="no-day" ref={side}>
          <SectionTitle id="no-day" count={waiting}>
            {noDay}
          </SectionTitle>
          {unscheduled}
        </aside>
      </div>
    );
  }
  return (
    <div className="tm-days-phone">
      <div className="tm-days-switch">
        <SegmentedControl
          label="Show"
          value={mode}
          onChange={(v) => setMode(v as "days" | "none")}
          options={[
            { value: "days", label: "By day" },
            { value: "none", label: waiting > 0 ? `${noDay} · ${waiting}` : noDay },
          ]}
        />
      </div>
      <div className="tm-days-scroll" ref={phone}>
        {tip}
        {mode === "days" ? days : unscheduled}
      </div>
    </div>
  );
}

function Gap({ row, numbered, today }: { row: GapRow; numbered: boolean; today: string | null }) {
  const days = row.first === row.last ? `Day ${row.first}` : `Days ${row.first}–${row.last}`;
  const dates =
    row.firstDate && row.lastDate
      ? row.first === row.last
        ? fmt(SHORT, row.firstDate)
        : `${fmt(SHORT, row.firstDate)} – ${fmt(SHORT, row.lastDate)}`
      : null;
  const label = factLine([numbered || !dates ? days : null, dates, "nothing planned yet"]);
  const holdsToday =
    today !== null &&
    row.firstDate !== null &&
    row.lastDate !== null &&
    row.firstDate <= today &&
    today <= row.lastDate;
  return (
    <div className="tm-day tm-gap" {...(holdsToday ? { "data-today": "" } : {})}>
      <span />
      <p className="callout tm-muted">{label}</p>
    </div>
  );
}

function Day({
  row,
  plan,
  trip,
  weather,
  unit,
  now,
  today,
  wide,
  onOpen,
}: {
  row: DayRow;
  plan: Plan;
  trip: TripView;
  weather: WeatherDay | undefined;
  unit: Unit;
  now: Date;
  today: boolean;
  wide: boolean;
  onOpen: (id: string) => void;
}) {
  const date = row.date ? fmt(WEEKDAY, row.date) : null;
  const eyebrow = plan.numbered || !date ? `Day ${row.n}` : null;
  const stay = row.stay;
  // The day's weather, level with its date: under it on a phone, heading the day on a computer, whose
  // date column is too narrow for it.
  const sky = weather && (
    <div className="tm-day-weather">
      <DayWeather day={weather} unit={unit} named={weather.where.for !== "stay" || !stay?.tonight} now={now} />
    </div>
  );
  return (
    <section
      className="tm-day"
      aria-label={[eyebrow, date].filter(Boolean).join(", ")}
      {...(today ? { "data-today": "", "aria-current": "date" as const } : {})}
    >
      <div className="tm-day-side">
        {stay && (
          <span
            className={`tm-stay-rail${stay.railStart ? " is-start" : ""}${stay.railEnd ? " is-end" : ""}`}
            aria-hidden="true"
          />
        )}
        {eyebrow && <span className="eyebrow tm-sun-text">{eyebrow}</span>}
        <span className="title">{date ?? `Day ${row.n}`}</span>
        {!wide && sky}
        {stay && (
          <span className={`tm-stay caption${stay.tonight ? "" : " is-leaving"}`}>
            <Icon name="bed" size={16} />
            <span>
              {stay.tonight ? stay.item.title : `Check out, ${stay.item.title}`}
              {stay.railStart && stay.tonight && ` · ${stay.nights} ${stay.nights === 1 ? "night" : "nights"}`}
            </span>
          </span>
        )}
      </div>
      <div className="tm-day-body">
        {wide && sky}
        {row.lanes.length === 0 && <p className="caption tm-muted tm-day-free">Nothing else planned</p>}
        {row.lanes.map((lane) => (
          <div key={lane.key} className="tm-lane">
            <span className="tm-lane-label caption">{lane.label}</span>
            <div className="tm-lane-groups">
              {lane.groups.map((group) => (
                <div key={group.people?.join(",") ?? "all"} className={`tm-group${group.people ? " is-split" : ""}`}>
                  {group.people && <span className="tm-split-label caption">{names(group.people, trip)}</span>}
                  {group.blocks.map((block) => (
                    <BlockButton key={block.item.id} block={block} trip={trip} onOpen={onOpen} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BlockButton({ block, trip, onOpen }: { block: Block; trip: TripView; onOpen: (id: string) => void }) {
  const faces = trip.members.filter((m) => block.going.includes(m.id));
  // The last thing opened from Days, lit.
  const lit = useLastOpened() === block.item.id;
  return (
    <button
      type="button"
      className={`tm-block ${block.firm ? "is-firm" : "is-band"}${lit ? ` is-lit ${kindClass(kindOf(block.item))}` : ""}`}
      {...litProps(lit)}
      onClick={() => onOpen(block.item.id)}
      aria-label={`${block.time ? `${block.time}, ` : ""}${block.item.title}, ${block.detail}${faces.length ? `, ${names(block.going, trip)} going` : ""}`}
    >
      {block.time && <span className="tm-block-time">{block.time}</span>}
      <KindIcon item={block.item} size={16} />
      <span className="tm-block-text">
        <span className="callout tm-strong">{block.item.title}</span>
        <span className="caption tm-muted">
          {block.detail}
          <Picks item={block.item} />
        </span>
      </span>
      {faces.length > 0 && <AvatarStack people={faces} size={24} max={4} />}
    </button>
  );
}

function Unscheduled({
  plan,
  trip,
  items: all,
  onOpen,
  onChanged,
}: {
  plan: Plan;
  trip: TripView;
  items: Item[];
  onOpen: (id: string) => void;
  onChanged: () => void;
}) {
  // The last thing opened from Days, lit.
  const last = useLastOpened();
  // Plan for a day, under a row (PlanDay.tsx): the one whose form is open, and the line that opened it, given the
  // focus back when it closes.
  const [planning, setPlanning] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const closePlanning = (id: string) => {
    setPlanning(null);
    requestAnimationFrame(() =>
      list.current?.querySelector<HTMLElement>(`[data-plan="${CSS.escape(id)}"]`)?.focus({ preventScroll: true }),
    );
  };
  const section = (title: string, items: Item[], when: (i: Item) => string | null) =>
    items.length > 0 && (
      <section className="tm-unscheduled" aria-label={title}>
        <h3 className="eyebrow tm-muted">{title}</h3>
        <ul className="tm-rows">
          {items.map((item) => (
            <li key={item.id} className="tm-unscheduled-item">
              <button
                type="button"
                className={`tm-row${item.id === last ? ` is-lit ${kindClass(kindOf(item))}` : ""}`}
                {...litProps(item.id === last)}
                onClick={() => onOpen(item.id)}
              >
                <KindIcon item={item} size={16} suggested={waitingSuggestion(item, trip.members)} />
                <span className="tm-row-text">
                  <span className="callout">{item.title}</span>
                  <span className="caption tm-muted">
                    {[
                      when(item),
                      waitingSuggestion(item, trip.members)
                        ? originLabel(item)
                        : wantsLine(item, trip.members, trip.you),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    <Picks item={item} />
                  </span>
                </span>
                <AvatarStack people={wanting(item, trip.members)} size={24} max={3} />
              </button>
              {!readOnly &&
                plannable(item) &&
                (planning === item.id ? (
                  <PlanDayForm
                    item={item}
                    trip={trip}
                    items={all}
                    onChanged={onChanged}
                    onClose={() => closePlanning(item.id)}
                  />
                ) : (
                  <button
                    type="button"
                    className="tm-plan-line"
                    data-plan={item.id}
                    aria-label={`Plan for a day: ${item.title}`}
                    onClick={() => setPlanning(item.id)}
                  >
                    <Icon name="calendar" size={16} />
                    <span>Plan for a day</span>
                  </button>
                ))}
            </li>
          ))}
        </ul>
      </section>
    );
  // A read-only trip: nothing more gets a day, so nothing asks your AI to fit them in.
  const readOnly = useReadOnly();
  if (plan.sometime.length + plan.untimed.length === 0) {
    return <Empty>Nothing waiting for a day. What you want with no day yet waits here until it has one.</Empty>;
  }
  return (
    <div className="tm-unscheduled-list" ref={list}>
      <p className="caption tm-muted">
        {readOnly
          ? "Wanted, not placed."
          : "Wanted, not placed. Plan one for a day here, or ask your AI to fit them into your days."}
      </p>
      {section("Some time", plan.sometime, (i) => (i.when?.precision === "part" ? `Any ${i.when.part}` : null))}
      {section(readOnly ? "No time" : "No time yet", plan.untimed, () => null)}
    </div>
  );
}

function wanting(item: Item, members: Person[]): Person[] {
  const ids = item.interest.filter((x) => x.tier && x.tier !== "no").map((x) => x.person);
  return members.filter((m) => ids.includes(m.id));
}

function names(ids: string[], trip: TripView): string {
  const list = trip.members.filter((m) => ids.includes(m.id)).map((m) => (m.id === trip.you ? "You" : m.name));
  return list.length <= 1 ? (list[0] ?? "") : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;
}
